import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
  additionalStructuredData?: object[];
  hreflang?: Array<{lang: string, url: string}>;
  noindex?: boolean;
  nofollow?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = "ERHR Global - HR Consulting Boston MA | Strategic HR Solutions",
  description = "Boston HR consulting firm. Transitional HR, strategic planning, employee engagement & performance management for Massachusetts businesses. Contact us!",
  image = "https://erhrglobal.com/og-image.jpg",
  url = "https://erhrglobal.com/",
  type = "website",
  structuredData,
  additionalStructuredData,
  hreflang = [
    { lang: "en", url: url },
    { lang: "x-default", url: url }
  ],
  noindex = false,
  nofollow = false
}) => {
  const fullTitle = title.includes('ERHR Global') ? title : `${title} | ERHR Global`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="robots" content={`${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}, max-image-preview:large, max-snippet:-1, max-video-preview:-1`} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="ERHR Global" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Hreflang Tags */}
      {hreflang && hreflang.map((item, index) => (
        <link key={index} rel="alternate" hrefLang={item.lang} href={item.url} />
      ))}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Additional Structured Data (FAQ, Service, etc.) */}
      {additionalStructuredData && additionalStructuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
